import React, { useState } from "react";
import { useDocument } from "../loan_tool";
import { useUser } from "../../../hooks/user";
import { formatPhone } from "./form_utils";
import CREDIT_SCORE from "../../../constants/credit_score";
import { lowest_credit_score } from "../../../calculations/applicant";
import {
  loan_amount_first_mortgage,
  loan_to_value
} from "../../../calculations/loan";
import Image from "../../ui/image";
import "./css/ba_forms.scss";

function formatMoney(decimalVal) {
  const n = parseFloat(decimalVal.toString());
  if (!n || isNaN(n)) return "";
  return "$" + Math.round(n).toLocaleString("en-US");
}

function formatLtvPct(decimalVal) {
  const n = parseFloat(decimalVal.toString());
  if (!n || isNaN(n)) return "";
  return n.toFixed(2) + "%";
}

export default function PreQualLetter() {
  const [{ companyProfile }] = useUser();
  const [
    document,
    setDocument,
    { documentType, documentAccountProfile },
    actions
  ] = useDocument();

  const {
    applicant,
    property,
    property_to_be_sold,
    is_selling_property
  } = document;

  const loan = (document.loans && document.loans[0]) || document.loan || {};
  const borrowers = (applicant && applicant.borrowers) || [];

  // Lender: use the loan officer's selected loan from Summary Sheet ("I / We select Loan Number")
  const selectedLoanIndex =
    document.summary && document.summary.selection_loan_number
      ? parseInt(document.summary.selection_loan_number, 10) - 1
      : 0;
  const selectedLoan =
    (document.loans && document.loans[selectedLoanIndex]) || loan;
  const lender = selectedLoan.lender || "";
  const b1 = borrowers[0] || {};

  const today = new Date();
  const defaultDate = `${today.toLocaleString("en-US", { month: "long" })} ${today.getDate()}, ${today.getFullYear()}`;

  // Autofill the realtor block from the BUYER'S AGENT (Selling Agent) saved on the
  // Realtor Agent Information form (New Property page), stored under the
  // seller_agent key. (The Listing Agent block — buyer_agent — feeds the Open
  // House Flyer instead.) Editable here, and edits are written BACK to the Realtor
  // form (see persistRealtor). Cell -> "C:", email -> "E:".
  const sellingAgent =
    (document.realtor_contacts && document.realtor_contacts.seller_agent) || {};

  const [date, setDate] = useState(defaultDate);
  const [realtorName, setRealtorName] = useState(sellingAgent.name || "");
  const [realtorFirm, setRealtorFirm] = useState(sellingAgent.firm || "");
  const [realtorPhone, setRealtorPhone] = useState(sellingAgent.cell_phone || "");
  const [realtorEmail, setRealtorEmail] = useState(sellingAgent.email || "");

  // Two-way sync: when the LO fills/edits the realtor block on the letter, write
  // it back to the Realtor Agent Information form (realtor_contacts.seller_agent)
  // and save, so the form connected to New Property stays in sync. Called on blur
  // so we persist complete values without saving on every keystroke.
  function persistRealtor() {
    const rc = document.realtor_contacts || {};
    const updatedSelling = {
      ...(rc.seller_agent || {}),
      name: realtorName,
      firm: realtorFirm,
      cell_phone: realtorPhone,
      email: realtorEmail
    };
    setDocument({
      ...document,
      realtor_contacts: { ...rc, seller_agent: updatedSelling }
    });
    try {
      actions.saveDocument();
    } catch (e) {
      // non-fatal; the values remain on the letter
    }
  }


  // Borrower name
  const buyerName = [b1.first_name, b1.middle_name, b1.last_name].filter(Boolean).join(" ");

  // Property from New Property page (document.property fields: address, address2, city, state, zip)
  const prop = property || {};
  const propLine1 = [prop.address, prop.address2].filter(Boolean).join(" ");
  const propLine2 = [prop.city, prop.state].filter(Boolean).join(", ") +
    (prop.zip ? " " + prop.zip : "");
  const fullPropertyAddr = [propLine1, propLine2].filter(Boolean).join(", ");

  // Purchase Price: Sales Price / Offer Price (loan.offer_price)
  const purchasePrice = parseFloat(loan.offer_price) || 0;

  // Loan Amount (1st Mortgage) + LTV from calculations — same functions used on Down Payment page
  let ltvDisplay = "";
  let loanAmtDisplay = "";
  try {
    if (purchasePrice > 0) {
      const ltvDecimal = loan_to_value(
        documentType, loan, prop, property_to_be_sold, is_selling_property
      );
      const loanAmtDecimal = loan_amount_first_mortgage(
        documentType, loan, prop, property_to_be_sold, is_selling_property
      );
      ltvDisplay = formatLtvPct(ltvDecimal);
      loanAmtDisplay = formatMoney(loanAmtDecimal);
    }
  } catch (e) {
    // If calculations fail (e.g. missing data), leave blank
  }

  // Credit Score: lowest across all borrowers, same logic as Summary Sheet
  const creditScoreRangeId = lowest_credit_score(borrowers).toString();
  const creditScoreLabel = CREDIT_SCORE[creditScoreRangeId] || "";

  // Common Rating by range ID — keyed to credit_score.js constants (1=800+, 12=560-579)
  // Ranges 10-12 (600-619, 580-599, 560-579) have no common rating — adjective is omitted.
  const CREDIT_COMMON_RATING = {
    "1":  "Exceptional / Excellent", // 800+
    "2":  "Very Good / Excellent",   // 760-799
    "3":  "Outstanding",             // 740-759
    "4":  "Very Good",               // 720-739
    "5":  "Very Good",               // 700-719
    "6":  "Good",                    // 680-699
    "7":  "Good",                    // 660-679
    "8":  "Good",                    // 640-659
    "9":  "Fair",                    // 620-639
    "10": "",                        // 600-619 (no common rating)
    "11": "",                        // 580-599 (no common rating)
    "12": "",                        // 560-579 (no common rating)
  };
  const commonRating = CREDIT_COMMON_RATING[creditScoreRangeId] || "";
  // "an" before vowel-starting ratings (Exceptional, Outstanding); "a" for all others
  const creditArticle = commonRating && /^[aeiou]/i.test(commonRating) ? "an" : "a";

  // LO info
  const loName = [documentAccountProfile.first_name, documentAccountProfile.last_name]
    .filter(Boolean).join(" ");
  const loTitle = documentAccountProfile.title || "Loan Officer";

  const companyName = companyProfile.company_name || "";
  const companyAddr = [companyProfile.business_address, companyProfile.business_address_2]
    .filter(Boolean).join(" ");
  const companyCityLine =
    [companyProfile.city, companyProfile.state].filter(Boolean).join(", ") +
    (companyProfile.zip ? " " + companyProfile.zip : "");

  const realtorFirstName = realtorName.trim().split(" ")[0] || "________";

  function handleRealtorPhoneChange(e) {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    let formatted = digits;
    if (digits.length >= 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 3) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 0) {
      formatted = `(${digits}`;
    }
    setRealtorPhone(formatted);
  }

  return (
    <>
    <style>{`
      .pq-doc {
        font-size: 12pt !important;
        font-family: Arial, Helvetica, sans-serif !important;
      }
      .pq-doc .pq-plain-input,
      .pq-doc .pq-salutation,
      .pq-doc .pq-body-text,
      .pq-doc .pq-key-table td,
      .pq-doc .pq-sig-name-print,
      .pq-doc .pq-sig-title {
        font-size: 12pt !important;
        font-family: Arial, Helvetica, sans-serif !important;
      }
      .pq-doc .pq-sig-name-italic {
        font-size: 17pt !important;
        font-family: Georgia, "Times New Roman", serif !important;
      }
      .pq-doc .pq-footer,
      .pq-doc .pq-footer span,
      .pq-doc .pq-footer-line,
      .pq-doc .pq-footer-form-number {
        font-size: 10pt !important;
      }
    `}</style>
    <div className="pq-doc">

      {/* Centered logo */}
      <div className="pq-logo-center">
        <Image url="company/company-logo" className="pq-logo-img" />
      </div>

      {/* Date */}
      <div className="pq-date-row">
        <input
          className="pq-plain-input"
          type="text"
          value={date}
          onChange={e => setDate(e.target.value)}
        />
      </div>

      {/* Realtor block */}
      <div className="pq-realtor-block">
        <input
          className="pq-plain-input"
          type="text"
          placeholder="Realtor Name"
          value={realtorName}
          onChange={e => setRealtorName(e.target.value)}
          onBlur={persistRealtor}
        />
        <input
          className="pq-plain-input"
          type="text"
          placeholder="Realty Firm"
          value={realtorFirm}
          onChange={e => setRealtorFirm(e.target.value)}
          onBlur={persistRealtor}
        />
        <input
          className="pq-plain-input"
          type="text"
          placeholder="C: (XXX) XXX-XXXX"
          value={realtorPhone}
          onChange={handleRealtorPhoneChange}
          onBlur={persistRealtor}
        />
        <input
          className="pq-plain-input"
          type="text"
          placeholder="E: email@example.com"
          value={realtorEmail}
          onChange={e => setRealtorEmail(e.target.value)}
          onBlur={persistRealtor}
        />
      </div>

      {/* Standard letter spacing: a blank line between the inside address block
          and the salutation. */}
      <div className="pq-salutation" style={{ marginTop: "2em" }}>Dear {realtorFirstName},</div>

      {/* Key fields — no underlines, 12pt */}
      <table className="pq-key-table">
        <tbody>
          <tr>
            <td className="pq-key-label">BUYER:</td>
            <td className="pq-key-value">{buyerName}</td>
          </tr>
          <tr>
            <td className="pq-key-label">PROPERTY:</td>
            <td className="pq-key-value">{fullPropertyAddr}</td>
          </tr>
          <tr>
            <td className="pq-key-label">PURCHASE PRICE:</td>
            <td className="pq-key-value">{purchasePrice ? formatMoney(purchasePrice) : ""}</td>
          </tr>
          <tr>
            <td className="pq-key-label">LOAN AMOUNT:</td>
            <td className="pq-key-value">
              {ltvDisplay && loanAmtDisplay
                ? `${ltvDisplay} LTV (${loanAmtDisplay})`
                : ""}
            </td>
          </tr>
          <tr>
            <td className="pq-key-label">LENDER:</td>
            <td className="pq-key-value">{lender || "TBD"}</td>
          </tr>
        </tbody>
      </table>

      {/* Body paragraphs */}
      <p className="pq-body-text">
        Please use this letter as evidence the client is Pre-Qualified for the above loan amount. We
        have met with the above client, have an application in process, have discussed the above
        referenced clients&#x2019; financial situation in detail, and our borrower has {creditArticle}{commonRating ? ` ${commonRating}` : ""} credit history with credit scores that range from {creditScoreLabel || "___"}. The client has
        liquid funds available for this purchase transaction.
      </p>

      <p className="pq-body-text">
        Please note that final loan approval is conditional upon an acceptable appraisal and purchase
        contract, verifications, lender conditions and requirements as needed. Should you have any
        further questions, feel free to contact the undersigned.
      </p>

      <p className="pq-body-text">
        Cordially,<br />
        {companyName}
      </p>

      {/* Signature */}
      <div className="pq-sig-block">
        <div className="pq-sig-name-italic">{loName}</div>
        <div className="pq-sig-name-print">{loName}</div>
        <div className="pq-sig-title">{loTitle}</div>
      </div>

      {/* Footer — 2 lines, left-aligned, flexible for any company's data length */}
      <div className="pq-footer">
        {/* Line 1: phone · address · email */}
        <div className="pq-footer-line">
          {[
            companyProfile.contact_phone
              ? `Phone: ${formatPhone(companyProfile.contact_phone, companyProfile.extension)}`
              : null,
            [companyAddr, companyCityLine].filter(Boolean).join(", ") || null,
            documentAccountProfile.email || null,
          ]
            .filter(Boolean)
            .map((item, i) => (
              <span key={i}>{i > 0 ? " · " : ""}{item}</span>
            ))}
        </div>
        {/* Line 2: license numbers */}
        {(documentAccountProfile.state_real_estate_license || documentAccountProfile.nmls_number) && (
          <div className="pq-footer-line">
            {[
              documentAccountProfile.state_real_estate_license
                ? `DRE Lic. # ${documentAccountProfile.state_real_estate_license}`
                : null,
              documentAccountProfile.nmls_number
                ? `NMLS# ${documentAccountProfile.nmls_number}`
                : null,
            ]
              .filter(Boolean)
              .map((item, i) => (
                <span key={i}>{i > 0 ? " · " : ""}{item}</span>
              ))}
          </div>
        )}
        <div className="pq-footer-form-number">LANIS Form - (07/01/2026)</div>
      </div>
    </div>
    </>
  );
}
